import { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import FormField, { fieldInputStyle } from "../components/ui/FormField";
import Loading, { ErrorBox } from "../components/ui/Loading";
import ImageUploadField from "../components/ui/ImageUploadField";
import { getSitePublico, updateSitePublico } from "../services/sitePublicoService";
import {
  uploadGuiaHero,
  deleteGuiaHero,
  uploadGuiaPerfil,
  deleteGuiaPerfil,
} from "../services/uploadsService";
import DepoimentosSection from "../components/site/DepoimentosSection";
import RoteirosSiteSection from "../components/site/RoteirosSiteSection";

const EMPTY = {
  slug: "",
  empresa: "",
  slogan: "",
  sloganIt: "",
  sitePublicado: false,
  bio: "",
  bioIt: "",
  cidade: "",
  anosExperiencia: "",
  telefone: "",
  email: "",
  instagram: "",
  facebook: "",
  logoUrl: "",
  heroImagemUrl: "",
  fotoPerfilUrl: "",
};

export default function SitePublicoPage() {
  const { C } = useTheme();
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  async function load() {
    try {
      const d = await getSitePublico();
      setForm({
        slug: d.slug ?? "",
        empresa: d.empresa ?? "",
        slogan: d.slogan ?? "",
        sloganIt: d.sloganIt ?? "",
        sitePublicado: !!d.sitePublicado,
        bio: d.bio ?? "",
        bioIt: d.bioIt ?? "",
        cidade: d.cidade ?? "",
        anosExperiencia: d.anosExperiencia != null ? String(d.anosExperiencia) : "",
        telefone: d.telefone ?? "",
        email: d.email ?? "",
        instagram: d.instagram ?? "",
        facebook: d.facebook ?? "",
        logoUrl: d.logoUrl ?? "",
        heroImagemUrl: d.heroImagemUrl ?? "",
        fotoPerfilUrl: d.fotoPerfilUrl ?? "",
      });
      setError(null);
    } catch {
      setError("Não foi possível carregar os dados do site.");
    } finally {
      setLoaded(true);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        slug: form.slug.trim() || null,
        slogan: form.slogan.trim() || null,
        sloganIt: form.sloganIt.trim() || null,
        sitePublicado: !!form.sitePublicado,
        bio: form.bio.trim() || null,
        bioIt: form.bioIt.trim() || null,
        cidade: form.cidade.trim() || null,
        anosExperiencia: form.anosExperiencia === "" ? null : Number(form.anosExperiencia),
        telefone: form.telefone.trim() || null,
        email: form.email.trim() || null,
        instagram: form.instagram.trim() || null,
        facebook: form.facebook.trim() || null,
      };
      await updateSitePublico(payload);
      setToast("Site atualizado.");
      setTimeout(() => setToast(null), 2500);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = fieldInputStyle(C);

  if (!loaded) return <Loading />;

  const publicUrl = form.slug
    ? `https://bragatoturismo.com.br`
    : "(defina um slug pra gerar a URL)";

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
          🌐 Site Público
        </h1>
        <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
          Configuração do site white-label · consumido por bragatoturismo.com.br
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          background: C.bgCard,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
          padding: 24,
          maxWidth: 820,
        }}
      >
        {/* ─── Seção 1 — Identidade ─── */}
        <div style={sectionLabel(C)}>Identidade</div>

        {/* Uploads do guia — hero (banner) + foto de perfil. Endpoints
            independentes do form; PUT /SitePublico/me não toca nessas URLs. */}
        {user?.guiaId && (
          <div
            style={{
              display: "flex",
              gap: 24,
              marginBottom: 18,
              flexWrap: "wrap",
            }}
          >
            <ImageUploadField
              label="Foto de perfil"
              hint="400×400 · cortada em círculo no site"
              shape="circle"
              width={150}
              height={150}
              value={form.fotoPerfilUrl || null}
              onUpload={async (file) => {
                const url = await uploadGuiaPerfil(user.guiaId, file);
                update("fotoPerfilUrl", url);
              }}
              onDelete={async () => {
                await deleteGuiaPerfil(user.guiaId);
                update("fotoPerfilUrl", "");
              }}
            />
            <ImageUploadField
              label="Imagem do hero"
              hint="1920×800 · banner do topo do site"
              shape="rect-16x9"
              width={400}
              height={null}
              value={form.heroImagemUrl || null}
              onUpload={async (file) => {
                const url = await uploadGuiaHero(user.guiaId, file);
                update("heroImagemUrl", url);
              }}
              onDelete={async () => {
                await deleteGuiaHero(user.guiaId);
                update("heroImagemUrl", "");
              }}
            />
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 8,
          }}
        >
          <FormField label="Slug (URL)" required hint="Apenas letras minúsculas, números e hífen. Ex: bragato">
            <input
              value={form.slug}
              onChange={(e) => update("slug", e.target.value)}
              style={inputStyle}
              placeholder="bragato"
            />
          </FormField>

          <FormField label="Nome do site" hint="Vem de Empresa (edita em Meu Perfil)">
            <input
              value={form.empresa}
              readOnly
              tabIndex={-1}
              style={{ ...inputStyle, opacity: 0.7, cursor: "default" }}
            />
          </FormField>

          <FormField label="Slogan (PT)" span={2}>
            <input
              value={form.slogan}
              onChange={(e) => update("slogan", e.target.value)}
              style={inputStyle}
              placeholder="Viagens privativas pela Itália que ficam na memória"
              maxLength={200}
            />
          </FormField>

          <FormField label="Slogan (IT)" span={2}>
            <input
              value={form.sloganIt}
              onChange={(e) => update("sloganIt", e.target.value)}
              style={inputStyle}
              placeholder="Viaggi privati in Italia..."
              maxLength={200}
            />
          </FormField>

          <FormField label="Site publicado" hint="Liga/desliga o site público">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "11px 14px",
                borderRadius: 10,
                border: `1px solid ${C.border}`,
                background: C.inputBg,
              }}
            >
              <input
                type="checkbox"
                checked={form.sitePublicado}
                onChange={(e) => update("sitePublicado", e.target.checked)}
                id="pub"
                style={{ transform: "scale(1.2)" }}
              />
              <label
                htmlFor="pub"
                style={{
                  fontSize: 13,
                  color: form.sitePublicado ? C.green : C.textMuted,
                  fontWeight: 600,
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {form.sitePublicado ? "Publicado ✓" : "Rascunho (site retorna 404)"}
              </label>
            </div>
          </FormField>

          <FormField label="URL pública" hint="Configure DNS externo apontando pra Hostinger">
            <input
              value={publicUrl}
              readOnly
              tabIndex={-1}
              style={{ ...inputStyle, opacity: 0.7, cursor: "default", color: C.accent }}
            />
          </FormField>
        </div>

        {/* ─── Seção 2 — Sobre ─── */}
        <div style={sectionLabel(C)}>Sobre</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 8,
          }}
        >
          <FormField label="Bio (PT)" span={2} hint="Até 2000 caracteres. Aparece na seção 'Sobre' do site.">
            <textarea
              value={form.bio}
              onChange={(e) => update("bio", e.target.value)}
              rows={6}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
              placeholder="Sou Luciana Bragato, acompanhante turística certificada..."
              maxLength={2000}
            />
          </FormField>

          <FormField label="Bio (IT)" span={2}>
            <textarea
              value={form.bioIt}
              onChange={(e) => update("bioIt", e.target.value)}
              rows={6}
              style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }}
              placeholder="Tradução italiana da bio..."
              maxLength={2000}
            />
          </FormField>

          <FormField label="Cidade" hint="Ex: Cossato, Piemonte, Itália">
            <input
              value={form.cidade}
              onChange={(e) => update("cidade", e.target.value)}
              style={inputStyle}
              maxLength={100}
            />
          </FormField>

          <FormField label="Anos de experiência">
            <input
              type="number"
              min="0"
              max="99"
              value={form.anosExperiencia}
              onChange={(e) => update("anosExperiencia", e.target.value)}
              style={inputStyle}
              placeholder="12"
            />
          </FormField>
        </div>

        {/* ─── Seção 3 — Contato & Redes ─── */}
        <div style={sectionLabel(C)}>Contato & Redes</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            marginBottom: 8,
          }}
        >
          <FormField label="WhatsApp / Telefone">
            <input
              value={form.telefone}
              onChange={(e) => update("telefone", e.target.value)}
              style={inputStyle}
              placeholder="+39 348 262 8202"
              maxLength={30}
            />
          </FormField>

          <FormField label="E-mail público">
            <input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              style={inputStyle}
              maxLength={150}
            />
          </FormField>

          <FormField label="Instagram (handle)" hint="Sem @. Ex: bragatoluciana">
            <input
              value={form.instagram}
              onChange={(e) => update("instagram", e.target.value)}
              style={inputStyle}
              placeholder="bragatoluciana"
              maxLength={50}
            />
          </FormField>

          <FormField label="Facebook (handle)" hint="Sem @. Ex: bragatoluciana">
            <input
              value={form.facebook}
              onChange={(e) => update("facebook", e.target.value)}
              style={inputStyle}
              placeholder="bragatoluciana"
              maxLength={50}
            />
          </FormField>
        </div>

        {error && (
          <div
            style={{
              marginTop: 14,
              padding: "10px 14px",
              background: C.redDim,
              color: C.red,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 22,
            paddingTop: 18,
            borderTop: `1px solid ${C.border}`,
          }}
        >
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: "10px 24px",
              borderRadius: 10,
              border: "none",
              background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})`,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.7 : 1,
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {saving ? "Salvando..." : "Salvar alterações"}
          </button>
        </div>
      </form>

      <DepoimentosSection />
      <RoteirosSiteSection />

      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "12px 20px",
            background: C.bgCard,
            border: `1px solid ${C.green || C.accent}`,
            borderRadius: 10,
            color: C.text,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            zIndex: 2000,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function sectionLabel(C) {
  return {
    fontSize: 11,
    fontWeight: 700,
    color: C.accent,
    textTransform: "uppercase",
    letterSpacing: 1,
    marginTop: 10,
    marginBottom: 12,
    paddingBottom: 6,
    borderBottom: `1px solid ${C.border}`,
  };
}
